import { TableActionsWrapper, TableBadgeLabel } from '@components'

import {
  Button,
  ButtonsGroup,
  Flex,
  FormItem,
  FormItemsGroup,
  Input,
  Modal,
  ModalProps,
  Space,
  Spacing,
  Table,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import type { ProductUnit } from '../../types/productUnit'
import { DeleteProductUnitsConfirmationModal } from './elements/DeleteProductUnitsConfirmationModal'
import { useProductUnitsListTableColumns } from './hooks/useProductUnitsListTableColumns'
import { useProductUnitsModal } from './hooks/useProductUnitsModal'

export interface ProductUnitOnSaveParams {
  lastUnitAdded: string | undefined
  units: ProductUnit[]
}

export interface ProductUnitsModalProps extends ModalProps {
  onClose: () => void
  onSave: (params: ProductUnitOnSaveParams) => void
  units: ProductUnit[]
}

const ProductUnitsModalComponent = ({
  onClose,
  onSave,
  units: baseUnits,
  ...rest
}: ProductUnitsModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeDeleteModal, open: openDeleteModal } = useModal(ModalId.DeleteProductUnitsConfirmationModal)

  const {
    handleAddUnit,
    handleUnitInputChange,
    isAddUnitDisabled,
    isUnitDuplicated,
    lastUnitAdded,
    setUnitToDelete,
    setUnits,
    tableUnits,
    unitToAdd,
    unitToDelete,
    units,
  } = useProductUnitsModal({ baseUnits })

  const handleRowDeleteActionClick = useCallback(
    (id: string) => () => {
      setUnitToDelete(id)
      openDeleteModal()
    },
    [openDeleteModal, setUnitToDelete],
  )

  const columns = useProductUnitsListTableColumns({ handleRowDeleteActionClick, t })

  const handleSaveClick = useCallback(() => {
    onSave({
      lastUnitAdded,
      units,
    })
  }, [lastUnitAdded, units, onSave])

  const handleModalConfirmDelete = useCallback(() => {
    setUnits((units) => units.filter((unit) => unit.name !== unitToDelete))
    setUnitToDelete(undefined)
    closeDeleteModal()
  }, [closeDeleteModal, unitToDelete, setUnitToDelete, setUnits])

  const handleModalClose = useCallback(() => {
    setUnitToDelete(undefined)
    closeDeleteModal()
  }, [closeDeleteModal, setUnitToDelete])

  return (
    <>
      <Modal size="s" {...rest}>
        <Modal.Header title={t('products.product_units_modal.title')} />

        <Modal.Body>
          <FormItemsGroup itemsInRow={1}>
            <FormItemsGroup rowSpacing={Spacing.S16}>
              <Text variant="h4">{t('products.product_units_modal.form.header')}</Text>

              <FormItemsGroup itemsInRow={4} columnSpacing={Spacing.S8}>
                <FormItem
                  error={isUnitDuplicated ? t('products.product_units_modal.form.error.unit_exists') : undefined}
                  itemColumnStart={1}
                  itemColumnEnd={4}
                  label={t('products.product_units_modal.form.unit')}
                  name="unit"
                >
                  <Input onChange={handleUnitInputChange} onPressEnter={handleAddUnit} size="sm" value={unitToAdd} />
                </FormItem>

                <Flex alignItems="flex-start" flexDirection="column">
                  <Space size="s24" />
                  <Button
                    disabled={isAddUnitDisabled}
                    fullWidth
                    icon="circlePlus"
                    onClick={handleAddUnit}
                    variant="outlined"
                  >
                    {t('products.product_units_modal.form.actions.add')}
                  </Button>
                </Flex>
              </FormItemsGroup>
            </FormItemsGroup>

            <TableActionsWrapper>
              <TableBadgeLabel amount={units.length} color="neutral">
                {t('products.product_units_modal.table.header')}
              </TableBadgeLabel>
            </TableActionsWrapper>

            <Table columns={columns} data={tableUnits} withSkeletonView />
          </FormItemsGroup>
        </Modal.Body>

        <Modal.Footer>
          <ButtonsGroup>
            <Button variant="text" onClick={onClose}>
              {t('products.product_units_modal.actions.cancel')}
            </Button>

            <Button onClick={handleSaveClick}>{t('products.product_units_modal.actions.save')}</Button>
          </ButtonsGroup>
        </Modal.Footer>
      </Modal>

      <DeleteProductUnitsConfirmationModal
        unit={unitToDelete}
        onClose={handleModalClose}
        onDelete={handleModalConfirmDelete}
      />
    </>
  )
}

export const ProductUnitsModal = withModalConditionalRender(ProductUnitsModalComponent)
